import React, { useState, useEffect } from "react";
import { Table, Button, Container, Spinner, Alert, Modal, Badge } from "react-bootstrap";

const Technicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [technicianToDelete, setTechnicianToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch all technicians from the backend
  const fetchTechnicians = async () => {
    try {
      const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
      const adminEmail = localStorage.getItem("adminEmail");
      
      // Prepare headers based on authentication type
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isStaticAdmin) {
        headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
        headers['x-static-admin-auth'] = 'true';
        headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
      } else {
        const token = localStorage.getItem("token");
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      let url = "https://madhusewingmachines.com/api/admin/technicians";
      if (isStaticAdmin) {
        url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();
      setTechnicians(data.data || data || []);
    } catch (err) {
      console.error('❌ Fetch technicians error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTechnicians();
  }, []);

  // Delete technician
  const handleDeleteClick = (technician) => {
    setTechnicianToDelete(technician);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!technicianToDelete) return;

    setDeleting(true);
    try {
      const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
      const adminEmail = localStorage.getItem("adminEmail");
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isStaticAdmin) {
        headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
        headers['x-static-admin-auth'] = 'true';
        headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
      } else {
        const token = localStorage.getItem("token");
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      let url = `https://madhusewingmachines.com/api/admin/technicians/${technicianToDelete._id}`;
      if (isStaticAdmin) {
        url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to delete technician");
      }

      // Remove technician from state
      setTechnicians(technicians.filter(tech => tech._id !== technicianToDelete._id));
      setShowDeleteModal(false);
      setTechnicianToDelete(null);
    } catch (err) {
      console.error('❌ Delete technician error:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Spinner animation="border" className="mt-4" />;
  if (error) return <Alert variant="danger">Error: {error}</Alert>;

  return (
    <Container className="mt-4">
      <h2>Technician Management</h2>
      <p className="text-muted">Manage all registered technicians</p>
      
      {technicians.length === 0 ? (
        <Alert variant="info">No technicians found</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Experience</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {technicians.map(technician => (
              <tr key={technician._id}>
                <td>{technician.name || 'N/A'}</td>
                <td>{technician.email || 'N/A'}</td>
                <td>{technician.phone || 'N/A'}</td>
                <td>{technician.address || 'N/A'}</td>
                <td>{technician.experience ? `${technician.experience} years` : 'N/A'}</td>
                <td>
                  <Badge bg={technician.isAvailable ? 'success' : 'secondary'}>
                    {technician.isAvailable ? 'Available' : 'Unavailable'}
                  </Badge>
                </td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(technician)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete technician <strong>{technicianToDelete?.name}</strong> ({technicianToDelete?.email})?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Technician'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Technicians;

